export const en = {
  appName: "Regime Job Sheet",
  appSubtitle: "Order & Workflow Platform",
  language: {
    label: "Language",
    en: "English",
    zhCN: "简体中文",
  },
  shell: {
    navOperations: "Operations",
    navInbox: "Inbox",
    navAdministration: "Administration",
    navSystem: "System",
    searchPlaceholder: "Search orders, documents, clients…",
    searchAria: "Open search",
    toggleSidebar: "Toggle sidebar",
    themeLight: "Switch to light mode",
    themeDark: "Switch to dark mode",
    profileMenu: "Profile menu",
    staffRoleLabel: "Tenant Staff",
    branchLabel: "Branch",
  },
  statuses: {
    order: {
      DRAFT: "Draft",
      SUBMITTED: "Submitted",
      PROCESSING: "Processing",
      WAITING_CLIENT: "Waiting on client",
      WAITING_LOGISTICS: "Waiting on logistics",
      ON_HOLD: "On hold",
      COMPLETED: "Completed",
      CLOSED: "Closed",
      CANCELLED: "Cancelled",
      REJECTED: "Rejected",
    },
    step: {
      PENDING: "Pending",
      IN_PROGRESS: "In progress",
      COMPLETED: "Completed",
      SKIPPED: "Skipped",
      REJECTED: "Rejected",
      WAITING: "Waiting",
    },
    approval: {
      PENDING_APPROVAL: "Pending approval",
      APPROVED: "Approved",
      REJECTED: "Rejected",
      REVISION_REQUESTED: "Revision requested",
      RESUBMITTED: "Resubmitted",
      CANCELLED: "Cancelled",
    },
    sla: {
      OK: "On track",
      WARNING: "Warning",
      APPROACHING: "Approaching",
      BREACHED: "Breached",
      OVERDUE: "Overdue",
      ESCALATED: "Escalated",
    },
    ticket: {
      OPEN: "Open",
      PENDING: "Pending",
      WAITING: "Waiting",
      RESOLVED: "Resolved",
      CLOSED: "Closed",
    },
  },
  validation: {
    required: "This field is required.",
    invalidEmail: "Invalid email address.",
    minLength: "Must be at least {min} characters.",
    networkError: "Network error. Please try again.",
    sessionExpired: "Session expired. Please sign in again.",
    somethingWrong: "Something went wrong.",
    noPermission: "You do not have permission to perform this action.",
  },
  common: {
    save: "Save",
    cancel: "Cancel",
    create: "Create",
    delete: "Delete",
    remove: "Remove",
    edit: "Edit",
    search: "Search",
    noResults: "No results",
    retry: "Retry",
    loading: "Loading…",
    pageNotFound: "Page Not Found",
    pageNotFoundHint: "The page you requested does not exist.",
    goHome: "Go home",
    copy: "Copy",
    copied: "Copied",
    enabled: "Enabled",
    disabled: "Disabled",
    enable: "Enable",
    disable: "Disable",
    actions: "Actions",
    all: "All",
    yes: "Yes",
    no: "No",
    none: "None",
    open: "Open",
    next: "Next",
    back: "Back",
    navGroup: "Navigation",
    prevPage: "Previous",
    nextPage: "Next",
    pageSummary: "Page {page} / {totalPages} · {total} total",
    rowsPerPage: "Rows per page",
    showingEntries: "Showing {start}–{end} of {total} entries",
    noData: "No data available",
    selectedCount: "{n} selected",
    searchPlaceholder: "Search…",
    confirm: "Confirm",
    loadFailed: "Failed to load",
    retryLater: "Please try again later",
    contactAdminLimit: "Limit reached. Please contact an administrator.",
    device: "Device",
    lastActive: "Last active",
    current: "Current",
    other: "Other",
    noSessions: "No sessions",
    invitedAt: "Invited at",
    noInvitations: "No invitations",
    actor: "Actor",
    resource: "Resource",
    time: "Time",
    provider: "Provider",
    model: "Model",
    openMenu: "Open menu",
    closeMenu: "Close menu",
    close: "Close",
    goTo: "Go to…",
    noCommands: "No matching commands",
  },
  login: {
    title: "Sign In",
    email: "Email",
    password: "Password",
    submit: "Sign in",
    failed: "Sign-in failed. Check your email and password.",
    unreachable: "Cannot reach the API. Confirm it is running.",
    forgot: "Forgot password",
    twoFactorTitle: "Two-Factor Authentication",
    twoFactorHint: "Enter the code from your authenticator app or email",
    twoFactorCode: "Verification code",
    twoFactorSubmit: "Verify and sign in",
    twoFactorBack: "Back",
    twoFactorFailed: "Invalid or expired verification code",
  },
  forgotPassword: {
    hint: "Enter your sign-in email and we will send a reset link.",
    title: "Reset Password",
    submit: "Send reset email",
    sent: "Email sent",
    back: "Back to sign in",
  },
  resetPassword: {
    title: "Set a New Password",
    newPassword: "New Password",
    confirmPassword: "Confirm Password",
    mismatch: "Passwords do not match",
    submit: "Reset password",
    ok: "Password reset successfully",
    fail: "Unable to reset password",
    missingToken: "This reset link is missing or invalid.",
  },
  verifyEmail: {
    title: "Verify Email",
    body: "Check your inbox and click the link to complete verification.",
    submit: "Verify email",
    done: "Email verified successfully.",
  },
  acceptInvite: {
    title: "Accept Invitation",
    password: "Password",
    confirmPassword: "Confirm Password",
    mismatch: "Passwords do not match",
    submit: "Accept invitation",
    ok: "Invitation accepted. You can now sign in.",
    fail: "Unable to accept invitation",
    goLogin: "Go to sign in",
  },
  twoFactorSetup: {
    title: "Set Up Two-Factor Authentication",
    body: "Scan the secret with an authenticator app, then enable.",
    enable: "Enable 2FA",
    disable: "Disable 2FA",
    enabled: "Enabled",
    disabled: "Not enabled",
    scanHint: "Scan the QR code or enter the secret manually",
    manualSecret: "Manual Secret",
    confirmCode: "Verification Code",
    confirm: "Confirm and enable",
    passwordConfirm: "Enter your sign-in password before disabling",
  },
  nav: {
    home: "Dashboard",
    orderServices: "Order Services",
    billingUpload: "Billing Upload",
    tasks: "Tasks",
    adminOverview: "Overview",
    organizations: "Organizations",
    users: "Users",
    roles: "Roles",
    teams: "Teams",
    workflows: "Workflows",
    tickets: "Tickets",
    approvals: "Approvals",
    exceptions: "Exceptions",
    announcements: "Announcements",
    connections: "Connections",
    settings: "Settings",
  },
  billingUpload: {
    title: "Billing Upload",
    description: "Find an order, choose a step, and upload billing documents.",
    searchLabel: "Search",
    searchPlaceholder: "Shipment reference, booking number, or container number",
    searchHint: "Shipment reference and booking number allow a partial word. A container number must match in full.",
    searching: "Searching…",
    notFound: "Record Not Found",
    notFoundHint: "Try another shipment reference, booking number, or container number.",
    clearSearch: "Clear Search",
    chooseOrder: "Choose Order",
    step: "Step",
    stepPlaceholder: "Select a step",
    noSteps: "This order has no step available.",
    noDocumentTypes: "No document type is available.",
    documentType: "Document Type",
    documentTypePlaceholder: "Select a document type",
    files: "Documents",
    filesHint: "Choose files again to add more. They upload together when you click Upload Documents.",
    upload: "Upload Documents",
    uploading: "Uploading…",
    uploaded: "Upload successful",
    uploadPartial: "Some files failed",
    uploadClosed: "This order is closed. Documents cannot be uploaded.",
    fileTooLarge: "File exceeds 5 MB.",
    fileType: "This file type is not supported.",
    filePending: "Ready",
    fileDone: "Uploaded",
    fileFailed: "Failed",
    removeFile: "Remove",
    uploadedTitle: "Uploaded Documents",
    uploadedEmpty: "No documents uploaded for this step.",
    uploadedPickStep: "Select a step to see its documents.",
  },
  dashboard: {
    title: "Operations Dashboard",
    description:
      "See new orders, tasks, waiting-on-client work, and SLA risk at a glance.",
    department: "Filter by department",
    departmentAll: "All departments",
    metricNew: "New Orders",
    metricInProgress: "In Progress",
    metricMyTasks: "My Tasks",
    metricTeamTasks: "Team Tasks",
    metricWaitingClient: "Waiting On Client",
    metricAwaitingDecision: "Awaiting Client Decision",
    metricOverdue: "Overdue",
    metricSlaWarning: "SLA Warning",
    metricCompletedToday: "Completed Today",
    viewOrders: "View Orders",
    viewExceptions: "View Exceptions",
    priorityQueue: "Priority Task Queue",
    priorityQueueDesc: "Highest-risk workflow steps across assigned and team work.",
    statusByDepartment: "Status by Department",
    statusByDepartmentDesc: "Active orders grouped by current workflow owner.",
    colOrder: "Order Service #",
    colClient: "Client / Client Branch",
    colActiveStep: "Active Workflow Step",
    colStatus: "Status",
    colProgress: "Progress",
    noPriorityTasks: "No active priority tasks.",
    noDepartmentStatus: "No department status available.",
    waitingClient: "waiting client",
    slaRisk: "SLA risk",
    empty: "No dashboard data yet",
    emptyDesc: "New Order Services will appear here",
  },
  exceptions: {
    title: "Exception Queue",
    description:
      "SLA breaches, rejected steps, pending approvals, and overdue due dates — handle these first.",
    countSummary: "{total} pending",
    empty: "No exceptions",
    emptyDesc: "Overdue steps, rejections, and pending approvals will appear here",
    emptyAction: "View order list",
    colSeverity: "Severity",
    colKind: "Type",
    colOrder: "Order",
    colClient: "Client",
    colStep: "Step",
    colMessage: "Details",
    colAt: "Time",
    actionView: "View order",
    severityDanger: "High",
    severityWarning: "Medium",
    kindSlaOverdue: "SLA overdue",
    kindStepRejected: "Step rejected",
    kindPendingApproval: "Pending approval",
    kindDueDateOverdue: "Due date overdue",
    prevPage: "Previous",
    nextPage: "Next",
    pageSummary: "Page {page} / {totalPages} · {total} total",
  },
  tasks: {
    title: "Tasks",
    description:
      "Workflow steps assigned to you or your team across all Order Services.",
    tabAll: "All active",
    tabMine: "My tasks",
    tabTeam: "Team tasks",
    tabOverdue: "Overdue",
    colOrder: "Order Service",
    colClient: "Client / Client Branch",
    colStore: "Client Branch",
    colStep: "Active Step",
    colSla: "SLA",
    colAssignee: "Assignee",
    empty: "No tasks",
    emptyDesc: "Assigned workflow work will appear here",
    openOrder: "Open order",
  },
  orderServices: {
    description: "Manage orders, ownership, workflow progress, and service exceptions.",
    title: "Order Services",
    search: "Search by order number, client, shipment reference, booking number, or container number",
    create: "New order",
    empty: "No order services",
    emptyDesc: "Create your first Order Service to get started",
    filterAll: "All",
    filterAssignedToMe: "Assigned to me",
    filterClient: "Client",
    filterStore: "Client Branch",
    filterOverdue: "Overdue",
    filterCompleted: "Completed",
    filterMyTasks: "My tasks",
    filterTeamTasks: "Team tasks",
    filterUnassigned: "Unassigned",
    filterExceptions: "Exceptions",
    colNumber: "Number",
    colService: "Service Type",
    colClient: "Client / Client Branch",
    colStore: "Client Branch",
    colStatus: "Status",
    colStep: "Current Step",
    colAssignee: "Assignee",
    colDue: "Due",
    colProgress: "Progress",
    unassigned: "Unassigned",
    overdue: "Overdue",
    slaWarning: "SLA warning",
    clientAction: "Client action required",
    backToList: "Back to list",
    prevPage: "Previous",
    nextPage: "Next",
    pageSummary: "Page {page} / {totalPages} · {total} total",
  },
  orderServiceDetail: {
    title: "Order Details",
    atAGlance: "At a Glance",
    serviceType: "Service Type",
    clientStore: "Client / Client Branch",
    status: "Status",
    activeStep: "Current Step",
    responsible: "Assignee",
    missingDocs: "Missing Documents",
    completedWork: "Completed Work",
    overdue: "Overdue",
    clientActionNeeded: "Client Action Needed",
    history: "History",
    nextAction: "Next Action",
    nextActionHint: "Next-action hint",
    nextActionShow: "Expand",
    nextActionHide: "Collapse",
    branch: "Logistics Branch",
    priority: "Priority",
    references: "References",
    workflow: "Workflow Steps",
    workflowStepsCount: "{count} steps",
    centerCurrent: "Jump to current step",
    noMissing: "No missing documents",
    noCompleted: "No completed steps yet",
    noHistory: "No history yet",
    overview: "Overview",
    documents: "Documents",
    comments: "Comments",
    audit: "Audit Trail",
    billing: "Billing",
    access: "Access",
    accessBypass: "System Admin and Manager have full access.",
    accessInheritedStaff: "Inherited via Team Responsibility",
    accessInheritedClient: "Inherited via Client Team End Customer Coverage",
    accessGrants: "People and Teams with Access",
    accessDerived: "Via Workflow Assignment",
    accessDerivedHint: "Assignment is not the same as an Access grant.",
    accessAddUser: "User",
    accessAddTeam: "Team",
    accessGrant: "Grant access",
    accessPrincipalUser: "User",
    accessPrincipalTeam: "Team",
    accessSourceCreator: "Creator",
    accessSourceExplicit: "Granted",
    commentsEmpty: "No comments yet",
    activityEmpty: "No activity yet",
    commentTimeline: "Timeline",
    commentAdd: "Add Comment",
    commentType: "Type",
    commentScope: "Scope",
    commentScopeOrder: "Order Service",
    commentCommented: "commented",
    commentRequestedDocument: "requested a document",
    commentRequestedClose: "requested closure",
    commentPlaceholder: "Write a comment…",
    commentPreset: "Preset Comment",
    commentPresetPlaceholder: "Choose a preset to fill the box…",
    commentPresetNone: "No presets",
    commentKindComment: "Comment",
    commentKindDocRequest: "Document request",
    commentKindCloseRequest: "Close request",
    commentInternal: "Internal",
    commentInternalHint: "Visible to Tenant Staff only — not shown to Client",
    commentSend: "Send",
    commentSendRequest: "Send document request",
    commentSendCloseRequest: "Send close request",
    commentOk: "Comment sent",
    commentFail: "Failed to send comment",
    approvals: "Approvals",
    approvalsEmpty: "No related approvals",
    requestApproval: "Request approval",
    approvalRequested: "Approval requested",
    approvalFail: "Approval action failed",
    noActiveStep: "No active step",
    noNextAction: "No next action",
    mandatory: "Required",
    optional: "Optional",
    unassigned: "Unassigned",
    progress: "Progress",
    summaryClient: "Organization",
    summaryStore: "Client Branch",
    summaryOwner: "Created by",
    exceptionCount: "{count} exceptions",
    documentCount: "{count} documents",
    shipmentDetails: "Shipment Details",
    shipmentVessel: "Vessel / voyage",
    shipmentVoyage: "Voyage",
    shipmentPol: "Port of loading (POL)",
    shipmentPod: "Port of discharge (POD)",
    shipmentRoute: "POL → POD",
    shipmentEta: "ETA / ETD",
    shipmentContainers: "Container Number",
    containerNumbersTitle: "Container Numbers",
    containerOpenCount: "View {count} numbers",
    containerPastePlaceholder:
      "Paste from Excel, one number per line or separated by commas.",
    containerValidCount: "{count} valid",
    containerInvalid:
      "Not an 11-character container number (4 letters and 7 digits).",
    containerTooMany: "At most 200 container numbers.",
    containerFilter: "Find a container number",
    containerNoMatch: "No matching container number",
    containerRemove: "Remove {number}",
    shipmentCargo: "Cargo / Volume",
    logisticsBranch: "Logistics Branch",
    lastUpdated: "Last Updated",
    billingAccount: "Billing Account",
    billingRef: "Billing Reference",
    actionSubmit: "Submit order",
    actionAdvance: "Advance step",
    actionHold: "Put on hold",
    resume: "Resume",
    requestClose: "Request close",
    requestCloseOk: "Close request sent",
    moreActions: "More actions",
    actionComplete: "Complete",
    actionClose: "Close",
    actionCancel: "Cancel",
    actionReject: "Reject",
    actionAssign: "Assign Owner",
    actionAssignStepOwner: "Assign Step Owner",
    assignmentTarget: "Step",
    actionReopen: "Admin reopen",
    reopenReason: "Reopen reason",
    reopenOk: "Reopened",
    reopenFail: "Failed to reopen",
    stepSkip: "Skip step",
    stepReopen: "Reopen step",
    stepRepeat: "Repeat step",
    stepReason: "Reason",
    stepAdd: "Add Step",
    stepStart: "Start Step",
    stepStartNeedsAssignee: "Assign an owner before starting this step.",
    stepStartOk: "Step started",
    stepEditProperties: "Edit Step Properties",
    stepSlaWarning: "SLA Warning (Minutes)",
    stepPropertiesOk: "Step properties saved",
    stepAddName: "Step Name",
    stepRequired: "Required Step",
    stepCanSkip: "Can skip",
    stepClientAction: "Client Action Required",
    stepClientConsent: "Client Consent Required",
    stepApprovalRequired: "Approval Required",
    stepSla: "SLA (minutes)",
    stepParallelGroup: "Parallel Group",
    stepParallelGroupNone: "None (run in sequence)",
    stepParallelGroupNew: "Create New Group…",
    stepParallelGroupHint:
      "Steps that share a group can be In Progress at the same time. Pick an existing group or create a short id (e.g. port_regime_bills).",
    stepParallelGroupNewPlaceholder: "e.g. port_regime_bills",
    stepAdvancedShow: "Show Advanced Settings",
    stepAdvancedHide: "Hide Advanced Settings",
    stepCanReopen: "Can reopen",
    stepCanRepeat: "Can repeat",
    stepDocumentRequirements: "Document Type Requirements",
    stepAddOk: "Step added",
    stepRemove: "Remove step",
    stepRemoveOk: "Step removed",
    stepMoveUp: "Move up",
    stepMoveDown: "Move down",
    stepReorderOk: "Order updated",
    checklist: "Document Checklist",
    checklistEmpty: "No checklist items",
    checklistAddExtra: "Add Extra document",
    checklistDocType: "Document Type",
    checklistAddOk: "Checklist item added",
    checklistAddFail: "Failed to add",
    remarksSave: "Save remarks",
    remarksOk: "Remarks saved",
    remarksFail: "Failed to save remarks",
    remarksPlaceholder: "Enter remarks…",
    linkMaster: "Link master document",
    linkMasterPick: "Select master document",
    linkMasterOk: "Master document linked",
    linkMasterFail: "Failed to link",
    linkMasterEmpty: "No master documents for this client",
    repeatOk: "Repeated step added",
    assignMember: "Select staff",
    assignTeam: "Select team",
    assignDepartment: "Select department",
    assignType: "Assignment Type",
    assignTypeUser: "Staff",
    assignTypeTeam: "Team",
    assignTypeDepartment: "Department",
    confirmCancel: "Cancel this order?",
    confirmReject: "Reject this order?",
    actionDone: "Action completed",
    actionFailed: "Action failed",
    actionEdit: "Edit fields",
    billingReference: "Billing Reference",
    editOk: "Saved",
    editFail: "Failed to save",
    download: "Download",
    preview: "Preview",
    versions: "Versions",
    versionsEmpty: "No previous versions",
    deleteDoc: "Delete document",
    deleteDocOk: "Deleted",
    deleteDocFail: "Failed to delete",
    recentUploads: "Recent uploads",
    commentDelete: "Delete",
    commentDeleteOk: "Comment deleted",
    commentDeleteFail: "Failed to delete",
    upload: "Upload document",
    takePhoto: "Take photo",
    chooseFile: "Choose file",
    uploading: "Uploading…",
    uploadOk: "Upload successful",
    uploadFail: "Upload failed. Please try again",
    verify: "Verify",
    verifyOk: "Verified",
    verifyFail: "Verification failed",
    pendingVerify: "Pending verification",
    pendingRelease: "Pending Release",
    releaseDocument: "Release",
    releaseDocumentOk: "Released to client",
    releaseDocumentFail: "Failed to release",
    releaseNeedsOtherStaff:
      "Release Document permission is required to publish this file to the Client Portal.",
    previewBlocked:
      "The browser blocked this preview. Use Download to open the file.",
    returnDocument: "Return",
    returnDocumentTitle: "Return Document",
    returnDocumentReason: "Reason",
    returnDocumentOk: "Returned to uploader",
    returnDocumentFail: "Failed to return",
    rejectDocument: "Reject document",
    rejectDocumentOk: "Document rejected",
    rejectDocumentFail: "Failed to reject",
    requestClient: "Request client action",
  },
  osd: {
    backToList: "Back to list",
    workflowTitle: "Workflow",
    workflowSubtitle: "{template} · {count} steps · click a step for details",
    progressSteps: "{done} / {total} required steps completed",
    progressDocs: "{done}/{total} mandatory documents",
    progressRef: "Ref {ref}",
    summaryClient: "Client",
    summaryStore: "Client Branch",
    summaryServiceType: "Service Type",
    summaryStatus: "Status",
    summaryBranch: "Branch",
    summaryOwner: "Owner",
    summaryActiveStep: "Active Step",
    summaryResponsible: "Responsible",
    nextAction: "Next Action",
    documentsTable: "Uploaded Documents",
    documentsColName: "Document",
    documentsColType: "Type",
    documentsColUploader: "Uploaded By",
    documentsColStatus: "Status",
    documentsColDate: "Uploaded",
    masterDocs: "Master Documents",
    masterDocsEmpty: "No master documents for this client",
    auditColTimestamp: "Timestamp",
    auditColUser: "User",
    auditColAction: "Action",
    auditColResource: "Resource",
    auditColIp: "IP",
    billingAccounts: "Billing Accounts",
    billingColName: "Account",
    billingColScope: "Scope",
    billingColReference: "External Reference",
    billingColStatus: "Status",
    billingScopeOrg: "Organization",
    billingScopeStore: "Client Branch",
    stepDrawerApproval: "Approval Required",
    stepDrawerParallel: "Parallel Group",
    stepDrawerSla: "SLA target",
    stepStartedAt: "Started At",
    stepCompletedAt: "Completed At",
    stepDrawerMinutes: "min",
    stepDrawerChecklist: "Step Documents",
    stepDrawerChecklistEmpty: "No documents linked to this step yet",
    stepDrawerDocumentsLoading: "Loading step documents…",
    stepDrawerDocumentsError: "Could Not Load Step Documents",
    stepDrawerStepOf: "Step {step} of {total}",
    stepDrawerComments: "Step Comments",
    stepDrawerCommentsEmpty: "No comments on this step",
    stepStatus: "Status",
    stepAssignedTo: "Assigned To",
    stepDurationSla: "Duration & SLA",
    stepElapsed: "Elapsed",
    stepOverdue: "Overdue",
    stepApproval: "Approval",
    stepApprovalNotRequired: "No approval required for this step.",
    stepExpectedType: "Expected Type",
    stepVerified: "Verified",
    stepUploaded: "Uploaded",
    stepNotUploaded: "Not Uploaded",
    stepCommentsError: "Could Not Load Step Comments",
    stepCommentPlaceholder: "Add a comment about this step…",
    bookingNumber: "Booking Number / BL Number",
    notProvided: "Not Provided",
    overviewSidebar: "Order Contacts and Alerts",
    clientContact: "Client Contact",
    assignedOwner: "Assigned Owner",
    exceptionsAlerts: "Exceptions & Alerts",
    noClientContact: "No Client Administrator Contact Available",
    contactUnavailable: "Client Contacts Unavailable",
    noOwner: "No Owner Assigned",
    noAlerts: "No Active Alerts",
    holdTitle: "Put Order On Hold",
    holdDescription: "Pause this Order Service until it is resumed.",
    uploadTitle: "Upload Document",
    uploadWorkflowStep: "Workflow Step",
    uploadDocumentType: "Document Type",
    uploadChecklistItem: "Checklist Item",
    linkToChecklistItem: "Link to Checklist Item",
    linkToChecklistHelp: "This file can be verified and counts toward the checklist.",
    uploadAsExtraHelp: "This file will be uploaded as Extra and cannot be verified.",
    uploadFile: "File",
    uploadBrowse: "Drop A File Or Click To Browse",
    uploadFormats: "PDF, XLS, XLSX, DOC, DOCX, JPG, PNG · Up To 5 MB",
    noUploadTarget: "No Document Type Available",
    noChecklistLink: "No Checklist Item Linked",
    noFileSelected: "No File Selected",
    fileTooLarge: "File Exceeds 5 MB",
    unsupportedFile: "Unsupported File Format",
    uploadNoteLabel: "Note (Optional · Saved As Step Comment)",
    uploadNotePlaceholder: "Add Context For The Reviewer…",
    uploadNoteFailed: "Note Not Saved",
    uploadStorageUnavailable: "Cannot reach file storage. Check that the storage project is active and browser uploads are allowed.",
    uploadStorageRejected: "File storage rejected the upload",
    requestClientTitle: "Request Client Action",
    messageToClient: "Message To Client",
    clientRequestPlaceholder: "Describe what the Client Organization needs to do…",
    clientRequestEffect: "This creates a step-linked request and may move the order to Waiting For Client.",
    sendClientRequest: "Send Request",
    manageSteps: "Manage steps",
    uploadDocument: "Upload Document",
    submitApproval: "Submit For Approval",
  },
  wizard: {
    title: "Create Order Service",
    stepOf: "Step {current} / {total}",
    steps: {
      SELECT_PARTIES: "Select Parties",
      SELECT_WORKFLOW_TEMPLATE: "Select Workflow Template",
      CUSTOMIZE_WORKFLOW: "Customize Workflow & Documents",
      ENTER_ORDER_INFO: "Enter Order Details",
      REVIEW_AND_SUBMIT: "Review And Submit",
    },
    clientOrg: "Client Organization",
    clientOrgRequired: "Please select a Client Organization",
    clientOrgSearch: "Search organizations…",
    endCustomer: "End Customer",
    endCustomerRequired: "Please select an End Customer",
    endCustomerSearch: "Search End Customers…",
    workflowTemplate: "Workflow Template",
    workflowTemplateRequired: "Please select a Workflow Template",
    workflowTemplateSearch: "Search Workflow Templates…",
    workflowTemplateHint: "Service Type is linked to the selected template.",
    logisticsBranch: "Logistics Branch",
    branchLockedHint: "Uses your current Branch context.",
    customerReference: "Customer Reference",
    shipmentReference: "Shipment Reference",
    priority: "Priority",
    remarks: "Remarks",
    store: "Client Branch (Optional)",
    storeSearch: "Search client branches…",
    storeNone: "No Client Branch",
    storeSkip: "Continue Without Client Branch",
    customizeHint:
      "Add or remove optional steps and document checklist items. Link master documents when needed.",
    addStep: "Add Step",
    addStepName: "Step Name",
    removeStep: "Remove Step",
    addChecklistItem: "Add Checklist Item",
    removeChecklistItem: "Remove",
    documentType: "Document Type",
    documentTypeSearch: "Search document types…",
    mandatory: "Mandatory",
    optional: "Optional",
    masterDocuments: "Master Documents",
    masterDocumentsHint:
      "Select a master document from this Client Organization to link onto the Order Service checklist.",
    masterPick: "Select Master Document",
    masterSearch: "Search master documents…",
    masterEmpty: "No master documents for this organization",
    masterLinkToChecklist: "Link To Checklist Item (Optional)",
    masterLink: "Link",
    masterLinked: "Linked",
    masterLinkOk: "Master document linked",
    checklistFromStepHint:
      "Documents required by workflow steps are edited in the step drawer. Only Extra checklist items can be removed here.",
    checklistFromStep: "From Step",
    checklistExtra: "Extra",
    uploadHint:
      "Take a photo or choose from gallery/files; mobile camera is supported.",
    reviewTitle: "Review Before Submit",
    reviewSteps: "Workflow Steps",
    reviewStepsAndChecklist: "Workflow Steps & Document Checklist",
    reviewExtraDocuments: "Extra Document",
    reviewChecklist: "Document Checklist",
    reviewMissing: "{count} Missing Mandatory Documents",
    reviewServiceType: "Service Type",
    reviewNumber: "Number",
    submit: "Submit Order",
    next: "Next",
    back: "Back",
    submitOk: "Submitted",
    submitting: "Submitting…",
    createFail: "Failed to create. Please try again",
    applyFail:
      "Order Service was saved as Draft, but some customizations could not be applied. Please finish on the detail page",
    templateLoadFail: "Failed to load Workflow Template. Please try again",
    updateFail: "Failed to save order details. Please try again",
    submitFail: "Failed to submit. Please try again",
    customizeFail: "Failed to update workflow. Please try again",
    priorityLow: "Low",
    priorityNormal: "Normal",
    priorityHigh: "High",
    priorityUrgent: "Urgent",
    none: "—",
  },
  admin: {
    title: "Admin Center",
    description:
      "Configure clients, people, catalog templates, finance references, and compliance tools.",
    sectionClientsPeople: "Clients & People",
    sectionCatalogTemplates: "Catalog & Templates",
    sectionFinanceCompliance: "Finance & Compliance",
    users: "Users",
    usersDesc: "Tenant Staff members and roles",
    organizations: "Organizations",
    organizationsDesc: "Client Organization list",
    stores: "Client Branches",
    storesDesc: "Client Branches under organizations",
    branches: "Logistics Branches",
    branchesDesc: "Logistics Branch (Workspace)",
    workflows: "Workflow Templates",
    workflowsDesc: "Default flows by service type",
    documentTypes: "Document Types",
    documentTypesDesc: "Document types and categories",
    commentPresets: "Comment Presets",
    commentPresetsDesc: "Canned replies for Tenant Staff comments",
    masterDocuments: "Master Documents",
    masterDocumentsDesc: "Reusable master documents per organization",
    serviceTypes: "Service Types",
    serviceTypesDesc: "Order Service types available to create",
    teams: "Teams / Departments",
    teamsDesc: "Teams for batch authorization (not Logistics Branch)",
    billing: "Billing Accounts",
    billingDesc: "Organization billing reference accounts",
    audit: "Audit Viewer",
    auditDesc: "Important action trail",
  },
  rolesAdmin: {
    title: "Roles & Permissions",
    description:
      "System role catalog and permission matrix. Assign roles to members on the Users page.",
    colRole: "Role",
    colCode: "Code",
    colScope: "Scope",
    colPermissions: "Permissions",
    colSystem: "System",
    systemYes: "System",
    systemNo: "Custom",
    assignCta: "Assign roles to users",
    permissionCount: "{n} permissions",
    empty: "No roles",
    emptyDesc: "Roles will appear after tenant bootstrap",
    addRole: "Add role",
    addPermission: "Add permission",
    save: "Save",
    saved: "Saved",
    saveFail: "Save failed",
    delete: "Delete",
    deleted: "Deleted",
    codePlaceholder: "Role code, e.g. BRANCH_VIEWER",
    namePlaceholder: "Role name",
    descriptionPlaceholder: "Permission description",
    permissionCatalog: "Permission catalog",
    permissionDescriptionLabel: "Description",
    noPermissions: "No permissions",
    systemReadOnly: "System permissions are code-managed and read-only here.",
    editRole: "Edit role",
    roleFormDescription: "Set the role scope and choose its system capabilities.",
    systemRoleReadOnly: "System roles are code-managed and read-only.",
    basicInformation: "Basic information",
    permissionMatrix: "Permission matrix",
    permissionMatrixHint: "Permissions are grouped by resource; selected capabilities are granted to this role.",
  },
  teamsAdmin: {
    title: "Teams / Departments",
    description:
      "Used for batch authorization and step assignment — not the same as a logistics branch.",
    create: "Create",
    edit: "Edit",
    save: "Save",
    saved: "Saved",
    saveFail: "Failed to save",
    empty: "No teams yet",
    emptyDesc: "Create your first team or department",
    colName: "Name",
    colType: "Type",
    colMembers: "Members",
    typeTeam: "Team",
    typeDepartment: "Department",
    created: "Created",
    createFail: "Failed to create",
    removed: "Deleted",
    removeFail: "Failed to delete",
    confirmRemove: "Delete this team?",
    members: "Members",
    addMember: "Add Member",
    memberUser: "Staff",
    memberSearch: "Search by name or email…",
    memberSearchEmpty: "No matching staff",
    memberAdded: "Member added",
    memberAddFail: "Failed to add",
    memberRemoved: "Member removed",
    memberRemoveFail: "Failed to remove",
    membersEmpty: "No members yet",
    roles: "Bound roles",
    rolesEmpty: "No bound roles",
    addRole: "Bind role",
    roleAdded: "Role bound",
    roleAddFail: "Failed to bind",
    roleRemoved: "Role removed",
    roleRemoveFail: "Failed to remove",
  },
  billingAdmin: {
    title: "Billing Accounts",
    description:
      "Organization billing reference data (not platform subscription).",
    create: "New account",
    empty: "No billing accounts",
    emptyDesc: "Add a billing reference for an organization",
    filterOrg: "Filter by organization",
    allOrgs: "All organizations",
    colName: "Name",
    colOrg: "Organization",
    colScope: "Scope",
    colRef: "External Reference",
    colStatus: "Status",
    scopeOrg: "Organization",
    scopeStore: "Client Branch",
    store: "Client Branch",
    active: "Active",
    inactive: "Inactive",
    deactivate: "Deactivate",
    confirmDeactivate: "Deactivate this account?",
    created: "Created",
    createFail: "Failed to create",
    deactivated: "Deactivated",
    deactivateFail: "Failed to deactivate",
    edit: "Edit",
    editTitle: "Edit Billing Account",
    save: "Save changes",
    updated: "Billing account updated",
    updateFail: "Failed to update",
  },
  approvals: {
    title: "Approval Inbox",
    description: "Approvals waiting on you or related to you.",
    empty: "No approvals",
    emptyDesc: "Approvals requested from workflow steps will appear here",
    colOrder: "Order",
    colStatus: "Status",
    colAttempt: "Attempts",
    colUpdated: "Updated",
    approve: "Approve",
    reject: "Reject",
    rejectReason: "Rejection reason",
    approved: "Approved",
    rejected: "Rejected",
    revision: "Request revision",
    resubmit: "Resubmit",
    revisionOk: "Revision requested",
    resubmitOk: "Resubmitted",
    actionFail: "Action failed",
  },
  clientConsent: {
    title: "Client Decision",
    staffDescription:
      "Actions waiting on the client. Staff cannot agree on their behalf.",
    hint: "This is one action inside the step. Completing the step is separate.",
    empty: "No Client Decisions",
    emptyDesc: "Permit and penalty requests appear here.",
    colOrder: "Order",
    colTitle: "Action",
    colDays: "Business Days",
    colStatus: "Status",
    statusPending: "Awaiting Client Decision",
    statusAgreed: "Agreed",
    statusRejected: "Rejected",
    statusWithdrawn: "Withdrawn",
    days: "About {n} business days",
    penalty: "Penalty Terms",
    summary: "What We Will Do",
    acknowledge: "Any penalty described above is payable by our organization.",
    agree: "Agree",
    reject: "Reject",
    rejectReason: "Rejection Reason",
    withdraw: "Withdraw",
    request: "Request Client Decision",
    requestTitle: "Action Title",
    requestSummary: "What We Will Do",
    requestDays: "Expected Business Days",
    requestPenalty: "Penalty Terms",
    send: "Send To Client",
    sent: "Request sent",
    saved: "Saved",
    withdrawn: "Withdrawn",
    fail: "Could not save",
  },
  organizations: {
    description: "Manage legal entities, contacts, End Customers, Client Branches, and access.",
    title: "Organizations",
    search: "Search by name or code",
    empty: "No organizations",
    emptyDesc: "Create your first organization",
    create: "New Organization",
    edit: "Edit",
    formName: "Name",
    basicInformation: "Basic Information",
    corporateIdentity: "Corporate Identity",
    legalName: "Company Name",
    tradeName: "Contact Person",
    registrationNumber: "Registration Number",
    entityType: "Designation",
    selectEntityType: "Select Entity Type",
    entityPrivateLimited: "Private Limited (Sdn. Bhd.)",
    entityPublicLimited: "Public Limited (Bhd.)",
    entityPartnership: "Partnership",
    entitySoleProprietorship: "Sole Proprietorship",
    entityLimitedLiabilityPartnership: "Limited Liability Partnership",
    selectCountry: "Select Country",
    registeredAddress: "Registered Address",
    operationalAddress: "Operational Address",
    businessEmail: "Email",
    supportPhone: "Phone Number",
    formEngagement: "Engagement",
    engagementDirect: "Direct client",
    engagementAgent: "Agent",
    created: "Created",
    createFail: "Failed to create",
    save: "Save",
    saved: "Saved",
    saveFail: "Failed to save",
    deactivate: "Deactivate",
    activate: "Activate",
    statusChanged: "Status updated",
    colName: "Name",
    colCode: "Code",
    colEngagement: "Engagement",
    colStores: "Client Branches",
    colMembers: "Members",
    colStatus: "Status",
    statusActive: "Active",
    statusInactive: "Inactive",
  },
  endCustomers: {
    title: "End Customers",
    description: "Manage end customers and their assigned client branches.",
    empty: "No End Customers yet",
    emptyDesc: "Add End Customers (e.g. BYD, Proton) under this Agent",
    create: "New End Customer",
    edit: "Edit",
    formName: "Name",
    formCode: "Code (Optional)",
    created: "Created",
    createFail: "Failed to create",
    save: "Save",
    saved: "Saved",
    saveFail: "Failed to save",
    remove: "Delete",
    removeOk: "Deleted",
    removeFail: "Failed to delete",
    removeConfirm: "Delete this End Customer?",
    colName: "Name",
    colCode: "Code",
    colStores: "Client Branches",
    colStatus: "Status",
    statusActive: "Active",
    statusInactive: "Inactive",
    deactivate: "Deactivate",
    activate: "Activate",
  },
  orgMembers: {
    back: "Back to Organizations",
    add: "Invite Member",
    empty: "No members yet",
    emptyDesc: "Invite a Client Admin, Client Staff, or Client Branch User",
    colName: "Name",
    colEmail: "Email",
    colRole: "Role",
    colEndCustomers: "End Customers",
    roleAdmin: "Client Admin",
    roleStaff: "Client Staff",
    roleStoreUser: "Client Branch User",
    userId: "User ID",
    inviteEmail: "Email",
    inviteHint:
      "We email an invite link. The invitee sets a password and joins this organization.",
    inviteOk: "Invitation sent",
    inviteFail: "Failed to send invitation",
    invitationsTitle: "Invitations",
    invitationsDesc: "People who were invited and have not joined yet.",
    colStatus: "Status",
    colExpires: "Expires",
    statusPending: "Pending",
    statusExpired: "Expired",
    emptyInvitations: "No open invitations",
    searchUser: "Search Client user",
    searchUserHint: "Type at least 2 characters of name or email",
    searchUserEmpty: "No matching Client users",
    pickUser: "Select user",
    changeRole: "Change role",
    assignEndCustomers: "Assigned End Customers",
    assignEndCustomersHint: "Required for Client Staff on Agent organizations",
    assignStores: "Assigned Client Branches",
    addOk: "Added",
    addFail: "Failed to add",
    roleUpdated: "Role updated",
    roleUpdateFail: "Failed to update role",
    removeOk: "Removed",
    removeFail: "Failed to remove",
    removeConfirm: "Remove this member?",
    notFound: "Organization not found",
  },
  orgTeamResponsibilities: {
    title: "Responsible Teams",
    description:
      "Staff Teams that inherit Access to all Order Services under this Client Organization. This is not RBAC Permission and not workflow assignment.",
    empty: "No responsible teams",
    add: "Add team",
    remove: "Remove",
    colTeam: "Team",
    addOk: "Responsibility added",
    addFail: "Failed to add responsibility",
    removeOk: "Removed",
    removeFail: "Failed to remove",
  },
  stores: {
    description: "Manage operating locations and their Organization ownership.",
    title: "Client Branches",
    search: "Search client branch, code, or organization",
    empty: "No client branches yet",
    emptyDesc: "Create a client branch under an organization",
    create: "New Client Branch",
    edit: "Edit",
    formName: "Name",
    formCode: "Code (optional)",
    formAddress: "Address (optional)",
    formOrg: "Organization",
    formEndCustomer: "End Customer",
    formEndCustomerRequired: "Select an End Customer for Agent organizations",
    created: "Created",
    createFail: "Failed to create",
    save: "Save",
    saved: "Saved",
    saveFail: "Failed to save",
    deactivate: "Deactivate",
    activate: "Activate",
    statusChanged: "Status updated",
    colName: "Client Branch",
    colCode: "Code",
    colOrg: "Organization",
    colEndCustomer: "End Customer",
    colStatus: "Status",
    statusActive: "Active",
    statusInactive: "Inactive",
  },
  workflows: {
    title: "Workflow Templates",
    empty: "No workflow templates",
    emptyDesc: "Configure default steps for a service type",
    create: "New template",
    created: "Created",
    createFail: "Failed to create",
    colName: "Template Name",
    colService: "Service Type",
    colSteps: "Steps",
    colVersion: "Version",
    colStatus: "Status",
    active: "Active",
    inactive: "Inactive",
    deactivate: "Deactivate",
    activate: "Activate",
    statusUpdated: "Status updated",
    statusFail: "Failed to update",
    editSteps: "Edit steps",
    stepsLabel: "Step List",
    stepName: "Step Name",
    addStep: "Add step",
    removeStep: "Remove",
    stepsRequired: "Add at least one step",
    stepsSaved: "Steps saved",
    stepsSaveFail: "Failed to save steps",
    stepRequired: "Required",
  },
  workflowsEditor: {
    title: "Workflow Template Editor",
    subtitle:
      "Configure reusable steps, SLA, assignment, and rules for each service type.",
    templateOverview: "Template Overview",
    templateOverviewDesc:
      "New order services inherit these steps until the instance is customised.",
    templateSettings: "Template Settings",
    selectTemplate: "Template",
    newTemplate: "New template",
    save: "Save changes",
    saved: "Template saved",
    saveFail: "Failed to save template",
    created: "Template created",
    createFail: "Failed to create template",
    version: "Version",
    templateName: "Template Name",
    serviceType: "Service Type",
    templateActive: "Template Active",
    noServiceType: "No service type",
    stepsTitle: "Workflow Steps",
    stepCount: "{n} steps",
    addStep: "Add step",
    removeStep: "Remove step",
    moveUp: "Move up",
    moveDown: "Move down",
    dragToReorder: "Drag to reorder",
    stepProperties: "Step Properties",
    stepPropertiesDesc:
      "Rules applied when an order service is generated from this template.",
    selectStepHint:
      "Select a step to edit its properties, or use Template settings to edit the name and service type.",
    stepName: "Step Name",
    stepRequired: "Required Step",
    clientActionRequired: "Client Action Required",
    clientConsentRequired: "Client Consent Required",
    clientConsentHint: "One action inside the step. It does not block completion.",
    approvalRequired: "Approval Required",
    slaExpectedMinutes: "SLA (minutes)",
    slaPlaceholder: "e.g. 240",
    parallelGroupKey: "Parallel Group Key",
    parallelGroupPlaceholder: "Optional group id",
    canSkip: "Can skip",
    canReopen: "Can reopen",
    canRepeat: "Can repeat",
    slaWarningMinutes: "SLA Warning (Minutes)",
    defaultTeam: "Default Team",
    noTeam: "Unassigned",
    defaultDepartment: "Default Department",
    noDepartment: "Unassigned",
    defaultAssignee: "Default Assignee",
    noAssignee: "Unassigned",
    dependsOnSteps: "Depends on Steps",
    dependsOnNone: "No dependencies",
    documentRequirements: "Document Type Requirements",
    documentRequirementsHint: "Toggle types required for this step.",
    docMandatory: "Mandatory",
    duplicate: "Duplicate",
    duplicated: "Template duplicated — review and save",
    empty: "No workflow templates",
    emptyDesc: "Create a template to configure default workflow steps.",
    stepsRequired: "Add at least one named step",
    loading: "Loading template…",
    mandatory: "Mandatory",
    optional: "Optional",
    clientAction: "Client action",
    approval: "Approval",
    parallel: "Parallel",
  },
  masterDocuments: {
    title: "Master Documents",
    description:
      "Maintain reusable master documents per organization; order checklists can link them directly.",
    filterOrg: "Select organization",
    pickOrg: "Select an organization first",
    endCustomer: "End Customer",
    pickEndCustomer: "Select an End Customer",
    empty: "No master documents",
    emptyDesc: "Upload the first master document",
    upload: "Upload master document",
    colName: "File Name",
    colType: "Document Type",
    colVersion: "Version",
    colExpiry: "Expiry",
    colCreated: "Uploaded",
    colStatus: "Status",
    pendingRelease: "Pending Release",
    release: "Release",
    releaseOk: "Released to client",
    releaseFail: "Failed to release",
    return: "Return",
    returnTitle: "Return Document",
    returnReason: "Reason",
    returnOk: "Returned to uploader",
    returnFail: "Failed to return",
    docType: "Document Type",
    issueDate: "Issue Date (Optional)",
    expiryDate: "Expiry Date (Optional)",
    uploadOk: "Upload successful",
    uploadFail: "Upload failed",
    download: "Download",
    preview: "Preview",
    noExpiry: "No expiry",
    newVersion: "New Version",
    newVersionTitle: "Upload New Version",
    newVersionHint: "Replaces the current file and increments the version.",
    newVersionOk: "Version uploaded",
    delete: "Delete",
    deleteTitle: "Delete Master Document",
    deleteConfirm:
      "Soft-delete “{name}”? Linked order checklists keep history; this file leaves the library.",
    deleteOk: "Deleted",
    deleteFail: "Failed to delete",
  },
  documentTypesAdmin: {
    description: "Configure document requirements used by orders and workflows.",
    title: "Document Types",
    empty: "No document types",
    emptyDesc: "Configure uploadable document types",
    create: "New type",
    created: "Created",
    createFail: "Failed to create",
    colCode: "Code",
    colName: "Name",
    colCategory: "Category",
    colStatus: "Status",
    active: "Active",
    inactive: "Inactive",
    deactivate: "Deactivate",
    activate: "Activate",
    statusUpdated: "Status updated",
    statusFail: "Failed to update",
    edit: "Edit",
    editTitle: "Edit Document Type",
    save: "Save changes",
    updated: "Document type updated",
    updateFail: "Failed to update",
    colUiGroup: "UI Group / Code",
  },
  commentPresetsAdmin: {
    title: "Comment Presets",
    description:
      "Professional canned replies Tenant Staff can pick when commenting on Order Services.",
    empty: "No Comment Presets",
    emptyDesc: "Add presets so staff can post consistent, professional replies.",
    create: "New Preset",
    created: "Comment preset created",
    createFail: "Failed to create comment preset",
    colTitle: "Title",
    colBody: "Body",
    colKind: "Type Scope",
    colSort: "Sort Order",
    colStatus: "Status",
    kindAny: "Any (Comment / Document Request)",
    bodyPlaceholder: "Write the full reply text staff will insert…",
    active: "Active",
    inactive: "Inactive",
    deactivate: "Deactivate",
    activate: "Activate",
    statusUpdated: "Status updated",
    statusFail: "Failed to update status",
    edit: "Edit",
    editTitle: "Edit Comment Preset",
    save: "Save Changes",
    updated: "Comment preset updated",
    updateFail: "Failed to update comment preset",
    delete: "Delete",
    deleted: "Comment preset deleted",
    deleteFail: "Failed to delete comment preset",
  },
  serviceTypesAdmin: {
    description: "Configure logistics services and their default workflows.",
    title: "Service Types",
    empty: "No service types",
    emptyDesc: "Configure creatable services and default templates",
    create: "New type",
    created: "Created",
    createFail: "Failed to create",
    colCode: "Code",
    colName: "Name",
    colRunningNumber: "Running Number",
    colTemplate: "Default Template",
    colStatus: "Status",
    active: "Active",
    inactive: "Inactive",
    noTemplate: "Not linked",
    deactivate: "Deactivate",
    activate: "Activate",
    statusUpdated: "Status updated",
    statusFail: "Failed to update",
    edit: "Edit",
    editTitle: "Edit Service Type",
    save: "Save changes",
    updated: "Service type updated",
    updateFail: "Failed to update",
    templateLabel: "Default Workflow Template",
    templateNone: "No template",
    openWorkflows: "Open workflows",
    runningNumberFormat: "Running Number Format",
    runningNumberHint: "Use #YY or #YYYY, #MM, and one sequence token such as #5. The sequence resets monthly in Malaysia time.",
    preview: "Preview",
  },
  home: {
    title: "Workbench",
    description:
      "Logistics company staff portal. Order Service / Workflow / Documents will connect in the Plugin phase.",
    welcomeQuestion: "What work do you mainly handle?",
    welcomeOps: "Operations & customer service",
    welcomeOpsDesc: "Handle day-to-day tickets and client communication",
    welcomeAdmin: "System administration",
    welcomeAdminDesc: "Members, branches, and platform configuration",
  },
  users: {
    description: "Manage Tenant Staff access, roles, and account status.",
    title: "Users",
    invite: "Invite Member",
    create: "Create User",
    createTitle: "Create User",
    createName: "Name",
    createEmail: "Email",
    createPassword: "Password",
    createPasswordHint: "At least 10 characters. The person can sign in with this password.",
    createBranch: "Logistics Branch",
    allBranches: "All Logistics Branches",
    createOk: "User created",
    createFail: "Failed to create user",
    search: "Search name, email, or role",
    empty: "No members yet",
    emptyDesc: "Invite your first colleague to collaborate",
    colName: "Name",
    colEmail: "Email",
    colRole: "Role",
    colBranches: "Logistics Branch",
    colStatus: "Status",
    statusActive: "Active",
    statusInvited: "Invited",
    statusDisabled: "Disabled",
    drawerTitle: "Member Details",
    changeRole: "Change role",
    saveRole: "Save role",
    roleUpdated: "Role updated",
    roleUpdateFail: "Failed to update role",
    editBranches: "Logistics Branches",
    saveBranches: "Save Branches",
    branchesUpdated: "Branches updated",
    branchesUpdateFail: "Failed to update branches",
    passwordResetTitle: "Password Reset",
    passwordResetHint:
      "Send a one-time reset link to this Tenant Staff member. Their current password is never shown.",
    passwordResetAction: "Send Reset Email",
    passwordResetSent: "Reset email sent",
    passwordResetFail: "Failed to send reset email",
    passwordChangeTitle: "Change Staff Password",
    passwordChangeAction: "Change Password",
    passwordChangeWarning:
      "This immediately changes the password and signs the Tenant Staff member out of every session.",
    newPassword: "New Password",
    confirmPassword: "Confirm Password",
    passwordMismatch: "Passwords do not match",
    passwordNeedsLetter: "Password must include a letter",
    passwordNeedsDigit: "Password must include a digit",
    passwordChanged: "Password changed",
    passwordChangeFail: "Failed to change password",
  },
  tickets: {
    description: "Review and resolve support requests from staff and Clients.",
    title: "Tickets",
    search: "Search subject or requester",
    empty: "No tickets yet",
    emptyDesc: "Tickets submitted by clients will appear here",
    colSubject: "Subject",
    colStatus: "Status",
    colPriority: "Priority",
    priorityLow: "Low",
    priorityNormal: "Normal",
    priorityHigh: "High",
    colRequester: "Requester",
    colAssignee: "Assignee",
    colUpdated: "Updated",
    internal: "Internal note",
    public: "Public reply",
    internalHint: "Visible to staff only — not sent to Client",
    publicHint: "Visible to Client",
    replyPlaceholder: "Write a reply…",
    sendInternal: "Send internal note",
    sendPublic: "Send public reply",
    unassigned: "Unassigned",
    statusLabel: "Status",
    assign: "Assign",
    saveAssign: "Save assignment",
    assignOk: "Assigned",
    assignFail: "Failed to assign",
    patchOk: "Updated",
    patchFail: "Failed to update",
    create: "New ticket",
    created: "Created",
    createFail: "Failed to create",
    createSubject: "Subject",
    createBody: "Body",
    statusOpen: "Open",
    statusPending: "Pending",
    statusWaiting: "Waiting",
    statusResolved: "Resolved",
    statusClosed: "Closed",
    forbidden: "You do not have permission for this ticket",
  },
  announcements: {
    description: "Publish and review operational notices for staff and Clients.",
    title: "Announcements",
    inbox: "My inbox",
    manage: "Manage publishing",
    publish: "Publish",
    edit: "Edit",
    saveDraft: "Save draft",
    publishOk: "Announcement published",
    publishFail: "Failed to publish announcement",
    unpublish: "Unpublish",
    empty: "No announcements",
    emptyDesc: "Publish the first announcement for staff or clients",
    inboxEmpty: "No announcements for you",
    colTitle: "Title",
    colAudience: "Audience",
    colStatus: "Status",
    colPublishedAt: "Published",
    formTitle: "Title",
    formBody: "Body",
    formAudience: "Audience",
    formStartsAt: "Start time (optional)",
    formEndsAt: "End time (optional)",
    audienceStaff: "Staff only",
    audienceClient: "Clients only",
    audienceBoth: "Staff and clients",
    published: "Published",
    read: "Read",
    unread: "Unread",
    markRead: "Mark as read",
    formHelper: "Keep operational updates clear and actionable.",
    preview: "Preview",
    previewEmpty: "Your announcement preview will appear here.",
    markdownHint: "Markdown supported",
    previewMarkdown: "Preview Markdown",
    editMarkdown: "Edit Markdown",
    inboxDescription: "Active staff announcements addressed to your account. Opening one marks it as read.",
  },
  connections: {
    title: "Connections",
    description: "Enable third-party integrations provisioned for your tenant.",
    empty: "No connections available",
    emptyDesc: "They appear here after the platform lists and authorizes them",
    all: "All",
    payment: "Payment",
    messaging: "Messaging",
    logistics: "Logistics",
    tenantEnabled: "Enabled for tenant",
    platformSuspended: "Suspended by platform",
    onboarding: "Onboarding status",
    missing: "Outstanding items",
    mode: "Onboarding mode",
    create: "New connection",
    created: "Created",
    createFail: "Failed to create",
    providerCode: "Provider Code",
    displayName: "Display name",
    category: "Category",
    test: "Connectivity test",
    testOk: "Test passed",
    testFail: "Test failed",
  },
  settings: {
    title: "Settings",
    description:
      "Organisation configuration for profile, security, branches, developer tools, and platform options.",
    personal: "Personal",
    profile: "Profile",
    password: "Password",
    security: "Two-Factor Authentication",
    sessions: "Active Sessions",
    notifications: "Notification Preferences",
    org: "Organization",
    organization: "Basic Info",
    members: "Members",
    invitations: "Invitations",
    audit: "Audit Log",
    branches: "Logistics Branches",
    verification: "Verification",
    developer: "Developer",
    apiKeys: "API Keys",
    webhooks: "Webhooks",
    platform: "Platform",
    platformEntitlements: "Entitlements & Modules",
    platformModule: "Feature Module",
    platformModulesEmpty: "No feature modules",
    platformModuleUpdated: "Feature module updated",
    platformAi: "AI Configuration",
    platformAiEmpty: "No AI Provider yet",
    platformConnectors: "Connector Catalog",
    platformRisk: "Risk & Circuit Breakers",
    platformOutOfScope:
      "Manage feature modules, email categories, and connected AI Providers.",
    emailCategories: "Email Categories",
    emailCategoriesHint:
      "Turn a category off to stop those emails. In-app notices still arrive. Each category also has a send cap so a repeating job cannot mail the same record all day.",
    emailCategoryLimitText: {
      order_service: "1 per completed action per day, up to 8 status emails per Order Service",
      task_assignment: "Up to 3 per workflow step per day. Each step has its own count",
      document_request: "Up to 3 per Order Service per day. Each Order Service has its own count",
      master_document_expiry: "Once per Client Organization per day",
      approval: "Once per approval per day",
      sla_alert: "Once per workflow step and status per day",
      ticketing: "Up to 8 per ticket per day",
      announcement: "Once per announcement per day",
      membership_invite: "Up to 3 per address per day",
      account_security: "Up to 8 per address per hour",
      system_error_msg: "Up to 10 per day",
    },
    emailCategoryUpdated: "Email category updated",
    emailCategoryCol: "Category",
    emailCategoryEmail: "Email",
    emailCategoryLimit: "Limit",
    emailCategoryOncePerDay: "Once per Client Organization per day",
    emailCategoryNoLimit: "Each event",
    emailCategoryRequired: "Required for Sign-in",
    emailCategoriesEmpty: "No email categories",
    emailCategoryLabel: {
      order_service: "Order Service",
      task_assignment: "Task Assignment",
      document_request: "Document Requests",
      master_document_expiry: "Master Document Expiry",
      approval: "Approvals",
      sla_alert: "SLA Alerts",
      ticketing: "Tickets",
      announcement: "Announcements",
      membership_invite: "Invitations",
      account_security: "Account Security",
      system_error_msg: "System Error Messages",
    },
    emailCategoryDescription: {
      order_service:
        "Order Service created, completed, closed, cancelled, or close requested.",
      task_assignment: "A workflow step was assigned or reassigned.",
      document_request: "A document was requested or rejected.",
      master_document_expiry:
        "Master documents nearing expiry. One digest per Client Organization per day.",
      approval: "Approval requested, completed, or rejected.",
      sla_alert: "SLA approaching, breached, or escalated.",
      ticketing: "Ticket assigned or a new ticket message.",
      announcement: "A published announcement.",
      membership_invite: "Staff and Client Organization invitation emails.",
      account_security:
        "Password reset, email verification, and sign-in codes.",
      system_error_msg: "Operational failure notices.",
    },
    auditDelay: "Records may lag by a few seconds",
    auditForbidden: "No permission to view audit logs",
    auditUnavailable:
      "Audit log list API is not exposed yet; this page is temporarily unavailable",
    auditSearch: "Search action or resource",
    auditAction: "Action",
    auditResourceType: "Resource type",
    auditFrom: "From",
    auditTo: "To",
    auditEmpty: "No audit records",
    auditLoadMore: "Next page",
    auditPrev: "Previous page",
    auditPageSummary: "Page {page} · {count} records",
    name: "Name",
    email: "Email",
    mobile: "Mobile (optional)",
    linkedInfo: "Linked information",
    roles: "Roles",
    teams: "Teams",
    departments: "Departments",
    noneAssigned: "None assigned",
    locale: "Language",
    orgName: "Organization name",
    timezone: "Timezone",
    currentPassword: "Current password",
    newPassword: "New password",
    channelInApp: "In-app",
    channelEmail: "Email",
    channelSms: "SMS",
    channelPush: "Push",
    typeCol: "Notification type",
    transactionalKeepOne:
      "This notification is required — keep at least one channel on.",
    inviteEmail: "Email",
    inviteRole: "Role",
    sendInvite: "Send Invitation",
    createBranch: "New Branch",
    branchName: "Name",
    branchSlug: "Slug",
    addressLine1: "Address Line 1",
    addressLine2: "Address Line 2 (Optional)",
    postalCode: "Zip / Postal Code",
    city: "City",
    state: "State",
    country: "Country",
    phoneNumber: "Phone number",
    archive: "Archive",
    branchMembers: "Branch members",
    branchAddMember: "Add Member",
    branchMemberUser: "Staff",
    branchMemberRole: "Branch role",
    branchMembersEmpty: "No members yet",
    branchMemberAdded: "Member added",
    branchMemberAddFail: "Failed to add",
    branchMemberRemoved: "Member removed",
    branchMemberRemoveFail: "Failed to remove",
    branchMemberRoleUpdated: "Role updated",
    branchMemberRoleFail: "Failed to update role",
    apiKeyName: "Name",
    apiKeyScopes: "Scopes",
    apiKeyScopesHint: "You can only select a subset of your own permissions",
    createApiKey: "Create API Key",
    apiKeyOnce:
      "The plaintext key is shown only once. Copy it now — you cannot view it again after closing.",
    revoke: "Revoke",
    rotateApiKey: "Rotate key",
    apiKeyRotated: "Key rotated",
    webhookUrl: "Callback URL",
    webhookEvents: "Subscribed events",
    addWebhook: "Add Endpoint",
    editWebhook: "Edit Endpoint",
    disableWebhook: "Disable",
    enableWebhook: "Enable",
    confirmDeleteWebhook: "Delete this Webhook?",
    webhookSaved: "Saved",
    webhookDeleted: "Deleted",
    rotateSecret: "Rotate Secret",
    sendTest: "Send test event",
    deliveries: "Delivery log",
    replay: "Replay",
    webhookEmpty: "No Webhooks yet",
    webhookForbidden: "Manage with System Admin",
    webhookTestSent: "Test event sent",
    webhookReplayed: "Replayed",
    apiKeyEmpty: "No API Keys yet",
    apiKeyForbidden: "Manage with System Admin",
    apiKeyRevoked: "Revoked",
    branchEmpty: "No branches yet",
    branchEmptyHint: "Manage branches with System Admin",
    requestFailed: "Request failed",
    saved: "Saved",
    passwordUpdated: "Password updated",
    colStatus: "Status",
    colEvents: "Events",
    colAttempts: "Attempts",
    colTime: "Time",
    colCreatedAt: "Created",
    colPrefix: "Prefix",
    memberCount: "Members",
    isDefault: "Default",
    defaultBadge: "Default",
    active: "active",
    disabled: "disabled",
    valid: "Valid",
    revokedStatus: "Revoked",
    tripCircuit: "Trip circuit breaker",
    circuitOpen: "Circuit breaker open",
    kycQueue: "Client KYC review",
    bpvQueue: "Sub-merchant BPV review",
    moduleOff: "Module not enabled",
  },
  branch: "Logistics branch",
  logout: "Sign out",
  notifications: {
    title: "Notifications",
    empty: "No notifications",
    markRead: "Mark as read",
    ariaLabel: "Notifications",
  },
} as const;

export type LocaleDict = typeof en;
